import type { BackgroundAudioOptions, VoiceAudioOptions } from '../audio/AudioController'
import type { DialogueLine } from '../ui/DialogueOverlay'
import type {
  ActionPromptCue,
  ExteriorActionCue,
  GameplayCue,
  LightingCue,
  PhoneReactionCue,
  PhoneReplyCue,
  SceneCue,
  SceneEventCue,
  SceneStateCue,
  SceneTimeline,
  TimelineCue,
  TimelineState,
  TransitionCue,
  VoiceTrack,
} from './types'

export interface TimelineRuntime {
  onCueStart?: (cue: TimelineCue, index: number) => void
  onComplete?: () => void
  unlockAudio: () => Promise<void>
  playBackgroundAudio: (source: string, options?: BackgroundAudioOptions) => Promise<void>
  stopBackgroundAudio: (fadeOut?: number) => void
  playVoiceAudio: (source: string, options?: VoiceAudioOptions) => Promise<void>
  stopVoiceAudio: (fadeOut?: number) => void
  playVideo: (source: string) => Promise<void>
  powerDownVideo: () => Promise<void>
  playCameraIntro: () => Promise<void>
  playKitchenCameraTour: () => Promise<void>
  playKitchenCameraTourStep: (stepIndex: number) => Promise<void>
  fadeLighting: (cue: LightingCue) => Promise<void>
  swapScene: (cue: SceneCue) => Promise<void>
  applySceneState: (cue: SceneStateCue) => Promise<void>
  showDialogue: (line: DialogueLine, voice?: VoiceTrack) => void
  showPhoneReaction: (cue: PhoneReactionCue) => void
  playPhoneReply: (cue: PhoneReplyCue) => Promise<void>
  hideDialogue: () => void
  playSceneEvent: (cue: SceneEventCue) => Promise<void>
  playTransition: (cue: TransitionCue) => Promise<void>
  playExteriorAction: (cue: ExteriorActionCue) => Promise<void>
  showActionPrompt: (cue: ActionPromptCue) => Promise<void>
  beginGameplay: (cue: GameplayCue) => void
  endGameplay?: (cue: GameplayCue) => void
}

const wait = (durationMs: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, Math.max(0, durationMs)))

const isDialoguePresentationCue = (cue: TimelineCue | undefined): boolean =>
  cue?.type === 'dialogue'
  || cue?.type === 'phone-reaction'
  || cue?.type === 'phone-reply'
  || (cue?.type === 'camera' && cue.keepDialogueVisible === true)

export class TimelinePlayer {
  private readonly timeline: SceneTimeline
  private readonly runtime: TimelineRuntime
  private cueIndex = -1
  private activeCue: TimelineCue | null = null
  private advanceResolve: (() => void) | null = null
  private gameplayGate: { id: string; resolve: () => void } | null = null
  private running = false
  private aborted = false

  public constructor(timeline: SceneTimeline, runtime: TimelineRuntime) {
    this.timeline = timeline
    this.runtime = runtime
  }

  public async run(): Promise<void> {
    if (this.running) throw new Error(`Timeline "${this.timeline.id}" is already running.`)
    this.running = true
    this.aborted = false

    try {
      for (let index = 0; index < this.timeline.cues.length; index += 1) {
        if (this.aborted) break
        const cue = this.timeline.cues[index]
        this.cueIndex = index
        this.activeCue = cue
        this.runtime.onCueStart?.(cue, index)
        await this.executeCue(cue)

        const nextCue = this.timeline.cues[index + 1]
        if (cue.type === 'dialogue') {
          const currentVoiceGroup = cue.voice?.group
          const nextVoiceGroup = nextCue?.type === 'dialogue' ? nextCue.voice?.group : undefined
          if (!currentVoiceGroup || currentVoiceGroup !== nextVoiceGroup) {
            this.runtime.stopVoiceAudio(0.12)
          }
        }
        if (isDialoguePresentationCue(cue) && !isDialoguePresentationCue(nextCue)) {
          this.runtime.hideDialogue()
        }
      }

      if (!this.aborted) this.runtime.onComplete?.()
    } finally {
      this.advanceResolve = null
      this.gameplayGate = null
      this.activeCue = null
      this.running = false
    }
  }

  private async executeCue(cue: TimelineCue): Promise<void> {
    switch (cue.type) {
      case 'audio':
        if (cue.action === 'unlock') {
          await this.runtime.unlockAudio()
        } else if (cue.action === 'play-background') {
          if (cue.source) await this.runtime.playBackgroundAudio(cue.source, cue.options)
        } else {
          this.runtime.stopBackgroundAudio(cue.fadeOut)
        }
        return

      case 'video':
        if (cue.action === 'play') await this.runtime.playVideo(cue.source)
        else await this.runtime.powerDownVideo()
        return

      case 'camera':
        if (cue.action === 'play-intro') await this.runtime.playCameraIntro()
        else if (cue.action === 'play-kitchen-tour') await this.runtime.playKitchenCameraTour()
        else await this.runtime.playKitchenCameraTourStep(cue.stepIndex)
        return

      case 'lighting':
        await this.runtime.fadeLighting(cue)
        return

      case 'scene':
        this.runtime.hideDialogue()
        await this.runtime.swapScene(cue)
        return

      case 'scene-state':
        this.runtime.hideDialogue()
        await this.runtime.applySceneState(cue)
        return

      case 'dialogue':
        this.runtime.showDialogue(cue.line, cue.voice)
        if (cue.voice?.source) {
          void this.runtime.playVoiceAudio(cue.voice.source, cue.voice.options).catch((error) => {
            console.warn(`Voice audio failed for timeline cue "${cue.id}".`, error)
          })
        }
        await this.waitForAdvance()
        return

      case 'phone-reaction':
        this.runtime.showPhoneReaction(cue)
        await this.waitForAdvance()
        return

      case 'phone-reply':
        await this.runtime.playPhoneReply(cue)
        await this.waitForAdvance()
        return

      case 'scene-event':
        this.runtime.hideDialogue()
        await this.runtime.playSceneEvent(cue)
        return

      case 'transition':
        this.runtime.hideDialogue()
        await this.runtime.playTransition(cue)
        return

      case 'exterior-action':
        this.runtime.hideDialogue()
        await this.runtime.playExteriorAction(cue)
        return

      case 'action-prompt':
        this.runtime.hideDialogue()
        await this.runtime.showActionPrompt(cue)
        return

      case 'wait':
        await wait(cue.durationMs)
        return

      case 'gameplay':
        this.runtime.hideDialogue()
        this.runtime.beginGameplay(cue)
        await this.waitForGameplay(cue.gameplayId)
        this.runtime.endGameplay?.(cue)
        return
    }
  }

  private waitForAdvance(): Promise<void> {
    if (this.aborted) return Promise.resolve()
    return new Promise((resolve) => {
      this.advanceResolve = resolve
    })
  }

  private waitForGameplay(gameplayId: string): Promise<void> {
    if (this.aborted) return Promise.resolve()
    return new Promise((resolve) => {
      this.gameplayGate = { id: gameplayId, resolve }
    })
  }

  public advance(): boolean {
    const resolve = this.advanceResolve
    if (!resolve) return false
    this.advanceResolve = null
    resolve()
    return true
  }

  public completeGameplay(gameplayId: string): boolean {
    const gate = this.gameplayGate
    if (!gate || gate.id !== gameplayId) return false
    this.gameplayGate = null
    gate.resolve()
    return true
  }

  public abort(): void {
    this.aborted = true
    const advanceResolve = this.advanceResolve
    const gameplayResolve = this.gameplayGate?.resolve
    this.advanceResolve = null
    this.gameplayGate = null
    advanceResolve?.()
    gameplayResolve?.()
    this.runtime.stopVoiceAudio(0)
  }

  public get isRunning(): boolean {
    return this.running
  }

  public get state(): TimelineState {
    return {
      timelineId: this.timeline.id,
      cueId: this.activeCue?.id ?? null,
      cueIndex: this.cueIndex,
      isRunning: this.running,
      waitState: this.advanceResolve ? 'advance' : this.gameplayGate ? 'gameplay' : null,
    }
  }
}
